import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Stethoscope, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const SITE = "https://telemednightingales.com";

export const SERVICES = {
  "cold-flu": {
    title: "Cold & Flu",
    url: `${SITE}/cold-%26-flu`,
    blurb: "Quick virtual or in-clinic evaluation for seasonal illness so you can start feeling better fast.",
    points: ["Assessment of fever, cough, sore throat and congestion", "Prescriptions sent to your pharmacy when appropriate", "Guidance on rest, fluids and when to seek further care"],
  },
  "womens-health": {
    title: "Women's Health",
    url: `${SITE}/womens-health`,
    blurb: "Discreet care for common women's health concerns, including infections and everyday needs.",
    points: ["UTI and vaginal infection evaluation and treatment", "Prescription refills and management", "Confidential, judgment-free virtual visits"],
  },
  "mens-health": {
    title: "Men's Health",
    url: `${SITE}/mens-health`,
    blurb: "Convenient, private consultations for men's health concerns from the comfort of home.",
    points: ["Erectile dysfunction consultation and treatment options", "General and preventive men's health questions", "Discreet phone or video visits"],
  },
  "chronic-care": {
    title: "Chronic Care & Refills",
    url: `${SITE}/home`,
    blurb: "Ongoing management of chronic conditions and hassle-free prescription refills.",
    points: ["Diabetes and blood pressure management", "Routine prescription refill visits", "Care coordinated with your pharmacy"],
  },
  "iv-hydration": {
    title: "IV Hydration Therapy",
    url: `${SITE}/iv-hydration`,
    blurb: "In-clinic hydration and wellness infusions to help you rehydrate, recover and feel your best.",
    points: ["Rehydration, immune support and recovery blends", "Vitamin and mineral infusions (B-complex, magnesium, vitamin C and more)", "Note: IV hydration is not FDA-approved to treat specific conditions"],
  },
  "skin-conditions": {
    title: "Skin Conditions",
    url: `${SITE}/skin-conditions`,
    blurb: "Virtual evaluation of common skin concerns with treatment sent to your pharmacy.",
    points: ["Rashes, acne and minor skin irritations", "Photo-based virtual assessment", "Prescriptions when appropriate"],
  },
  "infection": {
    title: "Infection",
    url: `${SITE}/infection`,
    blurb: "Prompt care for common infections so you can get treated without the wait.",
    points: ["Evaluation of common bacterial and viral infections", "Antibiotics prescribed when clinically appropriate", "Fast turnaround on virtual visits"],
  },
  "std-sexual-health": {
    title: "STD / Sexual Health",
    url: `${SITE}/std%2F-sexual-health`,
    blurb: "Confidential sexual health consultations and treatment guidance.",
    points: ["Private, judgment-free consultations", "Screening guidance and treatment", "Discreet handling of your information"],
  },
};

export default function TeleMedService() {
  const { slug } = useParams();
  const svc = SERVICES[slug];

  if (!svc) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <h1 className="font-heading text-2xl font-bold">Service not found</h1>
        <Button asChild className="mt-6 rounded-full"><Link to="/telemed">Back to TeleMed</Link></Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-14">
      <Link to="/telemed" data-testid="service-back" className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><ArrowLeft className="h-4 w-4" /> Back to TeleMed</Link>
      <div className="mt-5 flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary"><Stethoscope className="h-6 w-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{svc.title}</h1>
          <p className="text-sm text-muted-foreground">TeleMed service with Lucy Norrell, DNP</p>
        </div>
      </div>

      <p className="mt-6 text-base text-muted-foreground">{svc.blurb}</p>

      <div className="mt-6 rounded-2xl border border-border bg-card p-6">
        <h2 className="mb-4 font-heading text-lg font-bold">What's included</h2>
        <ul className="space-y-3">
          {svc.points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /> {p}</li>
          ))}
        </ul>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild size="lg" className="rounded-full px-7" data-testid="service-book">
          <Link to="/telemed">Request an appointment</Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="rounded-full px-7" data-testid="service-live-link">
          <a href={svc.url} target="_blank" rel="noreferrer">View live details on telemednightingales.com <ExternalLink className="ml-2 h-4 w-4" /></a>
        </Button>
      </div>
      <p className="mt-4 text-xs text-muted-foreground">Details are provided by TeleMed Nightingales. Visit their website for the most current information and pricing.</p>
    </div>
  );
}
